import React, { useEffect, useRef } from "react";
import { MicOff, VideoOff, Crown } from "lucide-react";

/** Video tile for LiveKit-powered Cloudivo Meet (Enterprise mode). Unlike the
 * legacy `VideoTile` (which binds a plain MediaStream via `srcObject`), LiveKit
 * hands us `Track` instances that must be `.attach()`ed to a DOM element. */
export function LiveKitVideoTile({ videoTrack, audioTrack, name, isHost, isSelf, audioOn, videoOn }) {
  const videoElRef = useRef(null);
  const audioElRef = useRef(null);

  useEffect(() => {
    const el = videoElRef.current;
    if (el && videoTrack) {
      videoTrack.attach(el);
      return () => videoTrack.detach(el);
    }
  }, [videoTrack]);

  useEffect(() => {
    const el = audioElRef.current;
    if (el && audioTrack && !isSelf) {
      audioTrack.attach(el);
      return () => audioTrack.detach(el);
    }
  }, [audioTrack, isSelf]);

  const showVideo = !!videoTrack && videoOn !== false;

  return (
    <div className="relative bg-slate-900/80 rounded-xl overflow-hidden aspect-video border border-white/5" data-testid={`livekit-video-tile-${isSelf ? "self" : name}`}>
      {showVideo ? (
        <video ref={videoElRef} autoPlay playsInline muted={isSelf} className="w-full h-full object-cover" />
      ) : (
        <div className="w-full h-full flex items-center justify-center">
          <div className="w-14 h-14 rounded-full bg-cyan-500/20 flex items-center justify-center text-cyan-300 font-bold text-lg">
            {name?.[0]?.toUpperCase() || "?"}
          </div>
        </div>
      )}
      {!isSelf && <audio ref={audioElRef} autoPlay />}
      <div className="absolute bottom-2 left-2 flex items-center gap-1.5 bg-black/50 backdrop-blur-sm rounded-md px-2 py-1">
        {isHost && <Crown className="w-3 h-3 text-amber-400" />}
        <span className="text-xs text-white font-medium truncate max-w-[100px]">{name}{isSelf ? " (You)" : ""}</span>
        {audioOn === false && <MicOff className="w-3 h-3 text-red-400" />}
      </div>
      {!showVideo && videoOn === false && videoTrack && (
        <div className="absolute top-2 right-2 bg-black/50 rounded-md p-1"><VideoOff className="w-3 h-3 text-slate-300" /></div>
      )}
    </div>
  );
}
